import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Lock, ArrowRight, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hammer & Anvil Forge Icon
const ForgeIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <path d="M6 28H34V32C34 33.1 33.1 34 32 34H8C6.9 34 6 33.1 6 32V28Z" fill="currentColor"/>
    <path d="M4 24H36V28H4V24Z" fill="currentColor"/>
    <path d="M10 20H30V24H10V20Z" fill="currentColor"/>
    <path d="M20 6L24 10L20 14L16 10L20 6Z" fill="#D4AF37"/>
    <path d="M18 12L22 16V20H18V16L18 12Z" fill="#D4AF37"/>
    <circle cx="12" cy="18" r="1" fill="#D4AF37"/>
    <circle cx="28" cy="16" r="1" fill="#D4AF37"/>
  </svg>
);

export default function ContractorLoginPage({ onContractorLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/contractor/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      toast.success("Welcome back!");
      onContractorLogin(response.data);
      navigate("/contractor/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 py-12 lg:px-16">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <Link to="/" className="inline-flex items-center mb-12">
            <img src="/logo.png" alt="DealForge" className="h-20 w-auto" />
          </Link>

          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 border border-gold/30 text-gold text-xs font-bold uppercase mb-4">
              <Briefcase className="w-4 h-4" />
              CONTRACTOR PORTAL
            </div>
            <h1 className="font-heading text-3xl font-bold text-white uppercase mb-2">
              Contractor Login
            </h1>
            <p className="text-white/70">
              Access your contractor dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-white/70">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                <Input
                  type="email"
                  placeholder="contractor@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/70">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white placeholder:text-white/70 h-12 rounded-none"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 gold-gradient-btn rounded-none font-bold uppercase tracking-wide"
              data-testid="login-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
              {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
            </Button>
          </form>

          <p className="mt-6 text-center text-white/70 text-sm">
            Don't have an account?{" "}
            <Link to="/contractor/register" className="text-gold hover:text-gold-light font-medium">
              Register as contractor
            </Link>
          </p>

          <p className="mt-4 text-center text-white/70 text-sm">
            Are you an investor?{" "}
            <Link to="/login" className="text-gold hover:text-gold-light font-medium">
              Login here
            </Link>
          </p>
        </div>
      </div>
      
      {/* Right Panel - Image */}
      <div className="hidden lg:flex w-1/2 bg-[#0A0A0A]/80 backdrop-blur-sm relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `url("https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }} />
        <div className="absolute inset-0 bg-gradient-to-bl from-black/80 to-transparent" />
        
        <div className="relative z-10 flex flex-col justify-center p-16">
          <div className="max-w-md">
            <h2 className="font-heading text-4xl font-black uppercase text-white mb-4">
              QUALIFIED DEALS WAITING
            </h2>
            <p className="text-white/70 text-lg">
              Real estate investors are posting renovation scopes right now. 
              Login to see available jobs and submit your bids.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
